import {
  GetAccount200ResponsePositionsInner,
  GetAccountAccumulatedFundings200Response,
  InstrumentTypeResponse,
} from "../../../../codegen-api";
import { apiNum, calculatePnl } from "../../../../utils/math";
import { resolveRoi } from "../../../../utils/positionRoi";
import { knownPrice } from "../../../../utils/unknownNumbers";
import { PosInfo } from "./types";

export function computePosInfo(
  position: GetAccount200ResponsePositionsInner | undefined,
  accumulatedFundingData: GetAccountAccumulatedFundings200Response | undefined
): PosInfo | undefined {
  if (!position) {
    return undefined;
  }
  // `undefined` when the backend could not resolve the market (null). Before,
  // `Number(null)` valued the position at $0 and its "PnL" at minus the whole
  // entry value — a fabricated total loss.
  const mark = knownPrice(position?.mark_price);
  const totalEntryValue =
    Number(position.avg_entry_price) * Number(position?.amount);
  // Prefer the API's mark-based values; fall back to client compute only when
  // absent AND a mark is known.
  const totalCurrentValue =
    apiNum(position.position_value) ??
    (mark !== undefined ? mark * Number(position?.amount) : undefined);
  const unrealizedPnl =
    apiNum(position.unrealized_pnl) ??
    (totalCurrentValue !== undefined
      ? calculatePnl(totalEntryValue, totalCurrentValue, position.side)
      : undefined);
  // Single source of truth for displayed ROE (utils/positionRoi.ts). Previously
  // this fell back to calculatePnlPercent, which divides by maintenance_margin,
  // while the positions table fell back to the margin_used-based chain — so the
  // same position could show two different ROEs whenever the API omitted
  // return_on_equity. resolveRoi gives every view one basis.
  const profitPercent =
    unrealizedPnl !== undefined ? resolveRoi(unrealizedPnl, position) : undefined;
  const accumulatedFundingRow = accumulatedFundingData && Array.isArray(accumulatedFundingData?.accumulated_fundings)
      ? accumulatedFundingData?.accumulated_fundings?.find(
          (row) => row.instrument_id === position.instrument_id
        )
      : undefined;

  const pendingFunding =
    position.instrument_type === InstrumentTypeResponse.Perpetual
      ? -Number(
          position.pending_funding_fee ||
            accumulatedFundingRow?.pending_funding_fee ||
            "0"
        )
      : undefined;
  const totalFunding =
    position.instrument_type === InstrumentTypeResponse.Perpetual
      ? -Number(
          position.total_funding_fee !== undefined
            ? position.total_funding_fee
            : accumulatedFundingRow?.total_funding_fee || "0"
        )
      : undefined;
  return {
    totalCurrentValue,
    unrealizedPnl,
    profitPercent,
    mark,
    pendingFunding,
    totalFunding,
  };
}
